import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import './ProjectContent.css';
import Loader from '../../components/loader/Loader';
import { useSelector } from 'react-redux';
import { enToHeActivityField } from '../../utils/activityFieldsDict';
import CrmUtils from '../../services/crm-utils/CrmUtils';
import { getCrmImageUrl } from '../../utils/imageUrl';
import CityAutocomplete from '../../components/city-autocomplete/CityAutocomplete';
import GlobalDesignedCheckbox from '../../components/global/GlobalDesignedCheckbox';
import ProjectsYouMightLike from '../../components/projects-you-might-like/ProjectsYouMightLike';
import HowItWorks from '../../components/how-it-works/HowItWorks';

const participationTypeDict = {
	IndividualsAndGroups: "יחידים וקבוצות",
	Individuals: "יחידים",
	Groups: "קבוצות",
	BusinessGroup: "קבוצה עסקית",
	Collaborations: "שיתופי פעולה"
};

// Helper to check which form types are allowed based on participationType
const getAllowedFormTypes = (participationType) => {
	switch (participationType) {
		case 'IndividualsAndGroups':
			return { individual: true, group: true, company: true };
		case 'Individuals':
			return { individual: true, group: false, company: false };
		case 'Groups':
			return { individual: false, group: true, company: true };
		case 'BusinessGroup':
			return { individual: false, group: false, company: true };
		case 'Collaborations':
		default:
			// Collaborations or undefined - all options open
			return { individual: true, group: true, company: true };
	}
};

function ProjectContent(props) {

	const store = useSelector(state => state.reducer)
	let { id } = useParams()
	const [projectData, setProjectData] = useState(false)
	const [showLoader, setShowLoader] = useState(true)
	const [signupError, setSignupError] = useState(false)
	const [thanksResponse, setThanksResponse] = useState(false)
	const partnerColor = props.partner.partnerConfig.leadingColor;
	const location = useLocation()
	const pageUrl = `${window.location.protocol}//${window.location.host}${location.pathname}`;

	const getWhatsappLink = () => {
		if (!projectData) return `https://api.whatsapp.com/send?text=${encodeURIComponent(pageUrl)}`;

		const shift = projectData.shifts?.[0];
		const date = projectData.date ? new Date(projectData.date).toLocaleDateString('he-IL') : '';
		const time = shift ? `${shift.startTime} - ${shift.endTime}` : '';
		const city = projectData.address?.city?.name || '';
		const org = projectData.owner?.name || '';

		const message = `🌟 הזדמנות להתנדב ביום מעשים טובים!

📌 ${projectData.name || ''}
🏢 ${org}
📍 ${city}
📅 ${date}
🕐 ${time}

${pageUrl}`;

		return `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
	};

	const getGmailLink = () => {
		if (!projectData) {
			const subject = encodeURIComponent("הזדמנות התנדבות מאתר יום מעשים טובים");
			const body = encodeURIComponent(`היי, רציתי לשתף אותך בהזדמנות התנדבות: ${pageUrl}`);
			return `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`;
		}

		const shift = projectData.shifts?.[0];
		const date = projectData.date ? new Date(projectData.date).toLocaleDateString('he-IL') : '';
		const time = shift ? `${shift.startTime} - ${shift.endTime}` : '';
		const city = projectData.address?.city?.name || '';
		const org = projectData.owner?.name || '';

		const subject = encodeURIComponent(`הזדמנות להתנדב: ${projectData.name || ''}`);
		const body = encodeURIComponent(`היי,

רציתי לשתף אותך בהזדמנות התנדבות ביום מעשים טובים:

${projectData.name || ''}
ארגון: ${org}
מיקום: ${city}
תאריך: ${date}
שעות: ${time}

לפרטים נוספים והרשמה:
${pageUrl}`);

		return `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`;
	};

	// Form state
	const [formType, setFormType] = useState('individual'); // 'individual', 'group', 'company'
	const [selectedShift, setSelectedShift] = useState('');
	const [volunteersCount, setVolunteersCount] = useState(1);
	const [groupName, setGroupName] = useState('');
	const [companyName, setCompanyName] = useState('');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [phone, setPhone] = useState('');
	const [email, setEmail] = useState('');
	const [city, setCity] = useState(null);
	const [newsletter, setNewsletter] = useState(false);
	const [validationErrors, setValidationErrors] = useState([]);
	const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
	const errorMessageRef = useRef(null);

	// Validation patterns
	const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	const israeliPhonePattern = /^(0[23489]\d{7}|05\d{8})$/; // Landline: 02/03/04/08/09 + 7 digits, Mobile: 05X + 8 digits

	// Form validation function
	const validateForm = () => {
		const errors = [];

		// Validate volunteers count (for group/company)
		if (formType !== 'individual') {
			const maxPlaces = getSelectedShiftInfo()?.availablePlaces || projectData?.shifts?.[0]?.availablePlaces || 999;
			if (!volunteersCount || volunteersCount < 1 || volunteersCount > maxPlaces) {
				errors.push({ field: 'volunteersCount', label: 'כמה תהיו?' });
			}
		}

		// Validate group name (for group/company)
		if (formType !== 'individual') {
			if (!groupName || groupName.trim() === '') {
				errors.push({ field: 'groupName', label: 'שם קבוצה' });
			}
		}

		// Validate company name (for company only)
		if (formType === 'company') {
			if (!companyName || companyName.trim() === '') {
				errors.push({ field: 'companyName', label: 'שם חברה' });
			}
		}

		// Validate first name
		if (!firstName || firstName.trim() === '') {
			errors.push({ field: 'firstName', label: 'שם פרטי' });
		}

		// Validate last name
		if (!lastName || lastName.trim() === '') {
			errors.push({ field: 'lastName', label: 'שם משפחה' });
		}

		// Validate phone
		const cleanPhone = phone.replace(/[-\s]/g, ''); // Remove dashes and spaces
		if (!cleanPhone || !israeliPhonePattern.test(cleanPhone)) {
			errors.push({ field: 'phone', label: 'טלפון' });
		}

		// Validate email
		if (!email || !emailPattern.test(email)) {
			errors.push({ field: 'email', label: 'אימייל' });
		}

		// Validate city
		if (!city) {
			errors.push({ field: 'city', label: 'מקום מגורים' });
		}

		setValidationErrors(errors);
		return errors.length === 0;
	};

	// Check if a field has validation error
	const hasError = (fieldName) => validationErrors.some(e => e.field === fieldName);

	// Error style for invalid fields
	const errorStyle = { borderColor: '#FF0000', color: '#FF0000' };

	// Re-validate on field changes after first submit attempt
	useEffect(() => {
		if (hasAttemptedSubmit) {
			validateForm();
		}
	}, [hasAttemptedSubmit, formType, volunteersCount, groupName, companyName, firstName, lastName, phone, email, city]);

	// Get allowed form types based on project's participationType
	const allowedFormTypes = getAllowedFormTypes(projectData?.attributes?.participationType);

	// Set initial form type to first allowed option when project loads
	useEffect(() => {
		if (projectData) {
			const allowed = getAllowedFormTypes(projectData.attributes?.participationType);
			// If current formType is not allowed, switch to first allowed one
			if (!allowed[formType]) {
				if (allowed.individual) setFormType('individual');
				else if (allowed.group) setFormType('group');
				else if (allowed.company) setFormType('company');
			}
		}
	}, [projectData]);

	useEffect(() => {
		const loadProject = async () => {
			setShowLoader(true)

			// Try to get project from Redux store first
			if (store.allProjects && store.allProjects.length > 0) {
				const project = store.allProjects.find(p => p._id === id)
				if (project) {
					setProjectData(project)
					if (project.shifts && project.shifts.length > 0) {
						const availableShift = project.shifts.find(s => s.availablePlaces >= 1);
						if (availableShift) setSelectedShift(availableShift._id)
					}
					setShowLoader(false)
					window.scrollTo(0, 0)
					return
				}
			}

			// If store is empty, fetch from API
			try {
				const response = await CrmUtils.getOrganizationProjects()
				if (response.data?.projects) {
					const project = response.data.projects.find(p => p._id === id)
					if (project) {
						setProjectData(project)
						if (project.shifts && project.shifts.length > 0) {
							const availableShift = project.shifts.find(s => s.availablePlaces >= 1);
							if (availableShift) setSelectedShift(availableShift._id)
						}
					}
				}
			} catch (error) {
				console.error('Error fetching project:', error)
			}

			setShowLoader(false)
			window.scrollTo(0, 0)
		}

		loadProject()
	},[id, store.allProjects])

	const getSelectedShiftInfo = () => {
		if (!projectData.shifts || !selectedShift) return null;
		return projectData.shifts.find(s => s._id === selectedShift);
	}

	const getFormType = () => {
		const typeMap = {
			'individual': 'Individual',
			'group': 'Group',
			'company': 'Corporate'
		};
		return typeMap[formType] || 'Individual';
	}

	const generateGoogleCalendarUrl = () => {
		if (!projectData) return '#';

		const shift = getSelectedShiftInfo();
		if (!shift) return '#';

		// Get the project date (or use a default date if not available)
		const projectDate = projectData.date ? new Date(projectData.date) : new Date();
		const year = projectDate.getFullYear();
		const month = String(projectDate.getMonth() + 1).padStart(2, '0');
		const day = String(projectDate.getDate()).padStart(2, '0');

		// Parse shift times (format: "HH:MM")
		const [startHour, startMin] = shift.startTime.split(':');
		const [endHour, endMin] = shift.endTime.split(':');

		// Format dates for Google Calendar (YYYYMMDDTHHMMSS)
		const startDate = `${year}${month}${day}T${startHour}${startMin}00`;
		const endDate = `${year}${month}${day}T${endHour}${endMin}00`;

		// Build event details
		const title = encodeURIComponent(projectData.name || 'התנדבות');
		const location = encodeURIComponent(projectData.address?.city?.name || '');
		const details = encodeURIComponent(
			`${projectData.description || ''}\n\nארגון: ${projectData.owner?.name || ''}`
		);

		return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${details}&location=${location}`;
	}

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Mark that user has attempted to submit
		setHasAttemptedSubmit(true);

		// Validate form before submission
		if (!validateForm()) {
			// Scroll to error message after state update (with offset)
			setTimeout(() => {
				if (errorMessageRef.current) {
					const yOffset = -100;
					const y = errorMessageRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
					window.scrollTo({ top: y, behavior: 'smooth' });
				}
			}, 100);
			return;
		}

		const formData = {
			volunteerData: {
				name: `${firstName} ${lastName}`.trim(),
				type: getFormType(),
				status: 'assigned',
				confirmationStatus: 'undone',
				numOfPeople: formType !== 'individual' ? volunteersCount : 1,
				budget: 0,
				triedToBeReached: 0,
				year: new Date().getFullYear(),
				address: {
					address: 'ללא כתובת',
					city: {
						_id: city?.id || ''
					},
					geo: {
						latitude: 0,
						longitude: 0
					}
				},
				preferences: {
					groupName: formType !== 'individual' ? groupName : '',
				},
				notes: [],
				description: ''
			},
			contactData: {
				contact: {
					name: `${firstName} ${lastName}`.trim(),
					email: email,
					phone: phone,
					newsletter: newsletter,
				},
				isPrimary: true
			},
			shiftId: selectedShift
		};

		try {
			const response = await CrmUtils.createVolunteerForProject(id, formData);
			console.log('Volunteer created:', response);
			setThanksResponse({
				Title: 'שמחים שבחרתם להשתתף ביום מעשים טובים.',
				SubTitle: 'מיד ישלח אליכם אי-מייל עם כל הפרטים',
				SubTitleNote: '(ממליצים לבדוק גם בתיקיית ה spam).',
				FaqLink: 'https://www.good-deeds-day.org.il/faq',
				ButtonLink: generateGoogleCalendarUrl(),
				ButtonTitle: 'הוספה ליומן'
			});
			// Reset validation state
			setValidationErrors([]);
			setHasAttemptedSubmit(false);
			// Scroll to top after successful form submission
			setTimeout(() => {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			}, 100);
		} catch (error) {
			console.error('Error creating volunteer:', error);
			setSignupError('אירעה שגיאה בשליחת הטופס. אנא נסו שנית.');
		}
	}

  return (
    <>
		<Loader showLoader={showLoader} color={partnerColor}/>
		<div id="main" role="main">
			<section className="record-form__section" id="first-content-section">
				<div className="content-container">
					<div className="record-form__wrapper">
						<div className="project-info">
							<div className="project-info__title">
								<h1>פרטים והרשמה להתנדבות</h1>
								<h2>{projectData.name}</h2>
								{/* <h3>{projectData.owner?.name}</h3> */}
								<h3></h3>
							</div>
							<div className="project-info__image">
								<div className="share">
									<div>שתפו</div>
									<a href={getWhatsappLink()} target='_blank' rel="noopener noreferrer">
										<svg width="16px" height="16px" viewBox="0 0 16 16" version="1.1" fill="#000" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
                                            <defs>
                                                <polygon id="path-1" points="0 0 16 0 16 16 0 16"></polygon>
                                            </defs>
                                            <g id="concept_1" stroke="none" strokeWidth="1" fillRule="evenodd">
                                                <g id="פרוייקט-טופס-מקום-עבודה" transform="translate(-961.000000, -1667.000000)">
                                                    <g id="Group-3" transform="translate(961.000000, 1667.000000)">
                                                        <mask id="mask-2" fill="white">
                                                            <use xlinkHref="#path-1"></use>
                                                        </mask>
                                                        <g id="Clip-2"></g>
                                                        <path d="M8.0000692,15.0828986 C6.66677047,15.0828986 5.36752048,14.7104387 4.2432201,14.0056575 C4.1704167,13.9598439 4.08612531,13.9356223 3.99948096,13.9356223 C3.95449788,13.9356223 3.90909956,13.9424043 3.8645317,13.9558301 L1.4231265,14.7073937 C1.60582704,14.0419208 1.92043184,12.9000424 2.11268264,12.230694 C2.14922274,12.1032189 2.12887655,11.9660551 2.05662679,11.8540818 C1.31129162,10.7073591 0.917239769,9.37461397 0.917239769,8.0000692 C0.917239769,4.09456829 4.09456829,0.917239769 8.0000692,0.917239769 C11.9055701,0.917239769 15.0828986,4.09456829 15.0828986,8.0000692 C15.0828986,11.9055701 11.9055701,15.0828986 8.0000692,15.0828986 M8.0000692,0 C3.58881997,0 0,3.58881997 0,8.0000692 C0,9.47938996 0.405955069,10.9214786 1.17468144,12.1750534 C0.993088176,12.8135364 0.705058002,13.8532686 0.339380098,15.1908581 L0.317788216,15.2701667 C0.273773995,15.4316906 0.321248454,15.6042872 0.441941539,15.7202744 C0.528032249,15.8031817 0.641251222,15.8488568 0.760837031,15.8488568 C0.806373758,15.8488568 0.851633665,15.8420748 0.895094248,15.8287874 L3.93719669,14.8928624 C5.16447374,15.6174361 6.56753086,16 8.0000692,16 C12.41118,16 16,12.41118 16,8.0000692 C16,3.58881997 12.41118,0 8.0000692,0 M10.2641718,11.9886158 C10.0083911,11.9886158 9.7261741,11.9657783 9.42541025,11.9205183 C6.40143946,11.4670888 4.4908347,8.45820466 3.90314795,6.93044057 C3.36127475,5.52253912 4.32931081,4.2360228 4.61457279,3.89982612 C4.75145979,3.8790647 4.89194543,3.86646943 4.99173868,3.86646943 C4.99589097,3.86646943 5.00004325,3.86646943 5.00405713,3.86646943 C5.24876513,4.24308169 5.84281871,5.39132691 5.88226542,5.59866435 C5.88101973,5.68835371 5.85499875,5.87645222 5.26302131,6.46884489 C5.17610014,6.55507401 5.12821045,6.67036912 5.12821045,6.79341517 C5.12821045,8.45266828 8.51550619,10.7210616 9.20672324,10.7210616 C9.6431284,10.7210616 10.1898459,10.2518534 10.8324812,9.32589382 C10.9403023,9.38374899 11.1303385,9.50804073 11.3307555,9.63911453 C11.618924,9.82748986 12.0066091,10.0809177 12.4633604,10.3469407 C12.2792758,11.1343524 11.8383031,11.9886158 10.2641718,11.9886158 M13.1998374,9.71081065 C12.6496596,9.40534088 12.1792057,9.09779496 11.8323515,8.8710802 C11.5641139,8.69585377 11.3703406,8.56934749 11.2214119,8.49460635 C11.0692999,8.41861954 10.9159422,8.3801417 10.7660447,8.3801417 C10.4620974,8.3801417 10.2861789,8.53419147 10.2399502,8.58069706 C10.2182199,8.60242736 10.198981,8.62595697 10.1826486,8.65059386 C9.7844445,9.24810768 9.34264137,9.71191792 9.21059871,9.79662454 C9.01281153,9.72520524 8.2636009,9.30402512 7.46663898,8.64021315 C6.69998875,8.00131489 6.18635109,7.37985623 6.07008711,6.95507747 C6.60061073,6.3977024 6.79950519,6.0217822 6.79950519,5.58676113 C6.79950519,5.10135901 5.78274899,3.32431941 5.60932188,3.15089231 C5.47312693,3.01497418 5.25942266,2.94895285 4.95602903,2.94895285 C4.76986825,2.94895285 4.54592168,2.97483542 4.2898641,3.02577012 C4.20128202,3.04376335 4.12045087,3.08694712 4.05595204,3.15103072 C3.97955,3.22743276 2.19628197,5.04765612 3.04694677,7.25971678 C3.95754289,9.62693449 6.29929325,12.379069 9.2892153,12.8275158 C9.62734972,12.8781737 9.95025909,12.9039179 10.2492236,12.9039179 C12.0590663,12.9039179 13.1291101,11.9897231 13.4293203,10.1873546 C13.4617081,9.99634945 13.369389,9.8047907 13.1998374,9.71081065" id="Fill-1" mask="url(#mask-2)"></path>
                                                    </g>
                                                </g>
                                            </g>
                                        </svg>
									</a>
									<a href={getGmailLink()} target="_blank" rel="noopener noreferrer">
										<svg width="23" height="16" viewBox="0 0 23 16" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" fill="#000">
                                            <defs>
                                                <path id="a" d="M0 0h22.98v15.908H0z"></path>
                                            </defs>
                                            <g fillRule="evenodd">
                                                <mask id="b" fill="#fff">
                                                    <use xlinkHref="#a"></use>
                                                </mask>
                                                <path d="M8.06 8.627l1.154 1.14a3.21 3.21 0 002.276.935c.86 0 1.669-.332 2.276-.935l1.156-1.14 6.423 6.332H1.636L8.06 8.627zm5.03.468a2.257 2.257 0 01-1.6.658 2.259 2.259 0 01-1.6-.659L1.635.949h19.71L13.09 9.095zM7.38 7.958L.958 14.29V1.62L7.38 7.958zm8.22 0l6.424-6.34V14.29L15.6 7.958zM21.132 0H1.849A1.844 1.844 0 000 1.835v12.239c0 1.011.83 1.834 1.85 1.834h19.282c1.02 0 1.849-.823 1.849-1.834V1.834A1.847 1.847 0 0021.13 0z" mask="url(#b)"></path>
                                            </g>
                                        </svg>
									</a>
								</div>
								<div className="img-holder">
									{(projectData?.image) ?
										<img src={getCrmImageUrl(projectData.image)} alt={"project image"} /> :
										<div className="placeholder image"></div>
									}
									{projectData.attributes?.activityField &&
										<h3 className="badge">{enToHeActivityField[projectData.attributes.activityField] || projectData.attributes.activityField}</h3>
									}
								</div>
							</div>
						</div>
						<div className="project-info__description">
							{/* <p className="subtitle">
								{projectData.attributes?.activityField && (
									<span>{enToHeActivityField[projectData.attributes.activityField] || projectData.attributes.activityField}.</span>
								)}
							</p> */}
							<p>{projectData.description}</p>
						</div>
						{ projectData &&
						<div className="contacts">
							<h4>חשוב לדעת לפני שנרשמים:</h4>
							<div className="contacts__info">
								<div className="card-info-item card-location"> 
									<svg className="location-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="22" viewBox="0 0 16 22" fill="#B60072">
										<path d="M7.997 10.481c-1.773 0-3.215-1.415-3.215-3.153 0-1.74 1.442-3.154 3.215-3.154 1.772 0 3.214 1.415 3.214 3.154 0 1.738-1.442 3.153-3.214 3.153m0-10.46C3.587.02 0 3.54 0 7.866c0 2.695 3.337 8.617 6.136 13.11A2.176 2.176 0 007.996 22c.766 0 1.462-.383 1.861-1.024 2.8-4.493 6.136-10.415 6.136-13.11 0-4.326-3.587-7.845-7.996-7.845" fillRule="evenodd"></path>
									</svg>
									<p>{projectData.address?.city?.name}</p>
								</div>
								{projectData.shifts && projectData.shifts.length > 0 && (
								<div className="card-info-item working-mode"> <svg xmlns="http://www.w3.org/2000/svg" width="18" height="16" viewBox="0 0 18 16">
										<g fill="#000" fillRule="nonzero">
											<path d="M.527 13h13.795a.51.51 0 0 0 .339-.13C14.793 12.752 17.816 9.98 18 4H3.184C3 9.429.217 11.98.188 12.006a.588.588 0 0 0-.156.624.53.53 0 0 0 .495.37zM17.466 1h-2.673V.5c0-.28-.235-.5-.534-.5-.3 0-.535.22-.535.5V1h-2.707V.5c0-.28-.236-.5-.535-.5-.3 0-.534.22-.534.5V1H7.276V.5c0-.28-.236-.5-.535-.5-.3 0-.534.22-.534.5V1H3.534C3.235 1 3 1.22 3 1.5V3h15V1.5c0-.28-.235-.5-.534-.5z"></path>
											<path d="M15.288 13.691c-.29.222-.655.343-1.03.343H3v1.475c0 .271.239.491.534.491h13.932c.295 0 .534-.22.534-.491V10c-1.03 2.324-2.468 3.503-2.712 3.691z"></path>
										</g>
									</svg>
									<p>{projectData.date ? new Date(projectData.date).toLocaleDateString('he-IL') : ''}</p>
								</div>)}
								<div className="card-info-item availablePlaces"> 
									<p>מספר המקומות הפנויים: {projectData.shifts?.reduce((sum, shift) => sum + (shift.availablePlaces || 0), 0) || 0}</p>
								</div>
								<div className="card-info-item singleOrGroup"> 
									<svg xmlns="http://www.w3.org/2000/svg" width="18" height="16" viewBox="0 0 18 16">
										<g fill="#000000" fillRule="nonzero">
											<circle cx="9" cy="3" r="3" />
											<circle cx="16" cy="4" r="2" />
											<circle cx="3" cy="4" r="2" />
											<path d="M4 8.526c-.645-.602-1.23-.523-1.976-.523C.908 8.003 0 9.031 0 10.293V14c0 .548.393.994.877.994 2.087 0 1.836.043 1.836-.102 0-2.626-.274-4.552 1.287-6.366zM9.891 7.044c-1.614-.137-3.016.002-4.226 1.019-2.025 1.65-1.635 3.874-1.635 6.593 0 .72.575 1.316 1.293 1.316 7.79 0 8.1.256 8.562-.785.151-.353.11-.24.11-3.61 0-2.677-2.276-4.533-4.104-4.533zM15.976 8.003c-.75 0-1.332-.078-1.976.523 1.549 1.8 1.287 3.594 1.287 6.365 0 .147-.208.103 1.805.103.5 0 .908-.462.908-1.03v-3.67c0-1.263-.908-2.29-2.024-2.29z" />
										</g>
									</svg>
									<p> הפרויקט עבור {participationTypeDict[projectData.attributes?.participationType] || projectData.attributes?.participationType || ''}</p>
								</div>
								<div className="card-info-item activityField"> 
									<svg width="20" height="19" viewBox="0 0 20 19" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M4.10884 14.6973L0.663265 10.5646C0.238095 10.0544 0 9.38435 0 8.68027V1.63265C0 0.734694 0.639456 0.00340136 1.42517 0H1.42857C1.77551 0 2.10884 0.142857 2.37075 0.404762C2.68367 0.714286 2.86054 1.16327 2.86054 1.63265V6.4898C2.35034 7.18367 2.38775 8.21089 2.97279 8.85714L5.19048 11.3129C5.35034 11.4898 5.62585 11.5068 5.80272 11.3435C5.97959 11.1837 5.9966 10.9082 5.83333 10.7313L3.61565 8.27551C3.28912 7.91156 3.28912 7.29932 3.61565 6.93537C3.61565 6.93537 3.61565 6.93537 3.61905 6.93197C3.76871 6.76871 3.95918 6.67687 4.16327 6.67687C4.36735 6.67687 4.56122 6.76871 4.70748 6.93197L8.77891 11.4422C9.28231 12 9.56803 12.7721 9.56803 13.5646V13.5714V18.7755H5.03061V17.2857C5.03061 16.3299 4.70408 15.4082 4.10884 14.6973ZM20 8.69728C20 9.39796 19.7653 10.0646 19.3401 10.5782L15.8946 14.7041C15.3027 15.415 14.9762 16.3333 14.9762 17.2959V18.7823H10.4422V13.5782C10.4422 12.7857 10.7313 12.0136 11.2313 11.4592L15.2959 6.95578C15.5918 6.62925 16.0884 6.62925 16.3844 6.95578C16.7109 7.31973 16.7109 7.93197 16.3844 8.29252L14.1701 10.7449C14.0102 10.9218 14.0238 11.1973 14.2007 11.3571C14.3776 11.517 14.6531 11.5034 14.8129 11.3265L17.0272 8.87415C17.6088 8.22789 17.6463 7.20408 17.1395 6.5068V1.65986C17.1395 1.19048 17.3163 0.741497 17.6293 0.431973C17.8912 0.173469 18.2211 0.0306123 18.568 0.0306123H18.5714C19.3571 0.0340137 19.9966 0.765306 19.9966 1.65986V8.69728H20Z" fill="black"/>
									</svg>
									<p>{enToHeActivityField[projectData.attributes?.activityField] || projectData.attributes?.activityField || ''}</p>
								</div>
								
							</div>
						</div>
						}

						{/* New Registration Form */}
						{!thanksResponse && projectData && (
						<form className="signup-form" onSubmit={handleSubmit} style={validationErrors.length > 0 ? { paddingTop: 0 } : {}}>
							{/* Validation Error Message */}
							{validationErrors.length > 0 && (
								<div ref={errorMessageRef} className="validation-error-message" style={{ color: '#FF0000', marginBottom: '15px' }}>
									<p>הטופס לא נשלח משום שחלק מהפרטים חסרים: {validationErrors.map(e => e.label).join(', ')}</p>
								</div>
							)}
							
							<h2 className="form-title">טופס שיבוץ לפעילות</h2>

							{/* Form Type Selector */}
							<p className="form-question">מי אתם?</p>
							<div className="form-type-selector">
								<button
									type="button"
									className={`type-btn ${formType === 'company' ? 'active' : ''}`}
									onClick={() => setFormType('company')}
									disabled={!allowedFormTypes.company}
								>
									חברה
								</button>
								<button
									type="button"
									className={`type-btn ${formType === 'group' ? 'active' : ''}`}
									onClick={() => setFormType('group')}
									disabled={!allowedFormTypes.group}
								>
									קבוצה
								</button>
								<button
									type="button"
									className={`type-btn ${formType === 'individual' ? 'active' : ''}`}
									onClick={() => setFormType('individual')}
									disabled={!allowedFormTypes.individual}
								>
									אדם פרטי
								</button>
							</div>

							{/* Volunteers Count - for group and company */}
							{formType !== 'individual' && (
								<div className="form-field inline-field ltr">
									<input
										type="number"
										min="1"
										value={volunteersCount}
										onChange={(e) => setVolunteersCount(parseInt(e.target.value) || 1)}
										className="count-input"
										style={hasError('volunteersCount') ? errorStyle : {}}
									/>
										<label className='rtl' style={hasError('volunteersCount') ? errorStyle : {}}>כמה תהיו?</label>
								</div>
							)}

							{hasError('volunteersCount') && (
								<p className="field-error-text" style={{ color: '#FF0000', fontSize: '14px', margin: '-15px 0 50px 0', width: '100%', textAlign: 'right' }}>
									כמות מתנדבים גדולה מכמות המקומות הפנויים
								</p>
							)}

							{/* Shift Selector */}
							{projectData.shifts && projectData.shifts.length > 0 && (
								<div className="form-field">
									<label className="field-label-bold">באילו שעות</label>
									<select
										value={selectedShift}
										onChange={(e) => setSelectedShift(e.target.value)}
										className="shift-select"
									>
										{projectData.shifts.filter(shift => shift.availablePlaces >= 1).map((shift) => (
											<option key={shift._id} value={shift._id}>
												{shift.startTime}-{shift.endTime}
											</option>
										))}
									</select>
									{getSelectedShiftInfo() && (
										<p className="available-spots">נותרו {getSelectedShiftInfo().availablePlaces} מקומות פנויים</p>
									)}
								</div>
							)}

							{/* Group Name - for group and company */}
							{formType !== 'individual' && (
								<><h3 className="section-title">פרטי הקבוצה</h3>
								<div className="form-field">
									<label className='ltr' style={hasError('groupName') ? errorStyle : {}}>שם קבוצה*</label>
									<input
										type="text"
										value={groupName}
										onChange={(e) => setGroupName(e.target.value)}
										style={hasError('groupName') ? errorStyle : {}}
									/>
								</div></>
							)}

							{/* Company Name - for company only */}
							{formType === 'company' && (
								<div className="form-field">
									<label className='ltr' style={hasError('companyName') ? errorStyle : {}}>שם חברה*</label>
									<input
										type="text"
										value={companyName}
										onChange={(e) => setCompanyName(e.target.value)}
										style={hasError('companyName') ? errorStyle : {}}
									/>
								</div>
							)}

							{/* Contact Details Section */}
							<h3 className="section-title">פרטי איש קשר</h3>

							<div className="form-field">
								<label className='ltr' style={hasError('firstName') ? errorStyle : {}}>שם פרטי*</label>
								<input
									type="text"
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									style={hasError('firstName') ? errorStyle : {}}
								/>
							</div>

							<div className="form-field">
								<label className='ltr' style={hasError('lastName') ? errorStyle : {}}>שם משפחה*</label>
								<input
									type="text"
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									style={hasError('lastName') ? errorStyle : {}}
								/>
							</div>

							<div className="form-field">
								<label className='ltr' style={hasError('phone') ? errorStyle : {}}>טלפון*</label>
								<input
									type="tel"
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									style={hasError('phone') ? errorStyle : {}}
								/>
							</div>

							<div className="form-field">
								<label className='ltr' style={hasError('email') ? errorStyle : {}}>אימייל*</label>
								<input
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									style={hasError('email') ? errorStyle : {}}
								/>
							</div>

							<div className="form-field">
								<label className='ltr' style={hasError('city') ? errorStyle : {}}>מקום מגורים*</label>
								<CityAutocomplete
									value={city}
									onChange={setCity}
									placeholder="ישוב"
									hasError={hasError('city')}
								/>
							</div>

							{/* Disclaimer */}
							<p className="disclaimer">*ההשתתפות הינה באחריות הבלעדית של הנרשמים</p>

							{/* Newsletter Checkbox */}
							<div className="checkbox-field">
								<GlobalDesignedCheckbox
									label="שלחו לי עוד דברים טובים מיום מעשים טובים, עמותת רוח טובה"
									checked={newsletter}
									onChange={(e) => setNewsletter(e.target.checked)}
									name="newsletter"
								/>
							</div>

							{/* Submit Button */}
							<button type="submit" className="btn btn-magenta submit-btn">שליחה</button>
						</form>
						)}

						{thanksResponse && !signupError && <div className="thanks">
							<div className="thanks__content">
								<h2 className="thanks__title" role="alert">{thanksResponse.Title}</h2>
								<p className="thanks__subtitle">
									{thanksResponse.SubTitle}
									<br />
									<span>{thanksResponse.SubTitleNote}</span>
								</p>
								<p className="thanks__faq">
									אם בינתיים יש לכם שאלות, היכנסו <a href={thanksResponse.FaqLink} target="_blank" rel="noopener noreferrer">לכאן</a> כדי לקבל את התשובות
								</p>
								<a href={thanksResponse.ButtonLink} target="_blank" rel="noopener noreferrer" className="btn btn-magenta thanks__btn">{thanksResponse.ButtonTitle}</a>
							</div>
						</div>}
						{signupError && !thanksResponse && <div className="error-message reg_error">
							<p>{signupError}</p>
						</div>}
					</div>
				</div>
			</section>

			{/* How It Works Section */}
			<HowItWorks isThankYou={!!thanksResponse} />

			{/* Projects You Might Like Section */}
			<ProjectsYouMightLike excludeProjectId={id} />
		</div>
	</>
  )
}

export default ProjectContent;
